import type { SoftphoneConnectionHook } from './index.types';
import type { SoftphoneConnectionState } from 'Modules/softphone/components/SoftphoneConnectionStatus/index.types';
import { useConnectionAnalytics } from 'Modules/softphone/hooks/useConnectionAnalytics';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { useCallback, useEffect, useRef, useState } from 'react';
import VoipMonitorService from 'Services/voipMonitorService';
import {
  ExtendedSession,
  VoipMonitorEvent,
} from 'Services/voipMonitorService/index.types';

export type { SoftphoneConnectionHook } from './index.types';

/**
 * Hook for connection quality monitoring.
 * Provides real-time monitoring of call connection quality.
 * Used by SoftphoneConnectionStatus component to display connection quality indicators.
 */
export const useSoftphoneConnectionStatus = (): SoftphoneConnectionHook => {
  const [connectionQuality, setConnectionQuality] =
    useState<SoftphoneConnectionState>('stable');
  const previousQualityRef = useRef<SoftphoneConnectionState>('stable');

  const { trackConnectionStatusChange } = useConnectionAnalytics();

  const isConnectionLost = useSoftphoneStore(
    (state) => state.status === 'disconnected'
  );

  // Derive currentCall directly from selector (avoids stale state)
  const currentCall = useSoftphoneStore((state) =>
    state.currentCallId
      ? state.activeCalls.get(state.currentCallId) ?? null
      : null
  );

  // Connection quality monitoring methods
  const startMonitoring = useCallback(() => {
    // Get fresh state to avoid stale closures
    const state = useSoftphoneStore.getState();
    const callId = state.currentCallId;
    const call = callId ? state.activeCalls.get(callId) : null;

    if (!call?.session || !callId) {
      return;
    }

    // Skip monitoring for conference participants — their peer connections
    // are closed after REFER/BYE, audio flows through the room session
    if (call.conferenceId && state.activeConference) {
      return;
    }

    const personId = call.personId ?? '';

    VoipMonitorService.startMonitoring({
      personId,
      callId,
      session: call.session as unknown as ExtendedSession,
    });
  }, []);

  const stopMonitoring = useCallback(() => {
    VoipMonitorService.stopMonitoring();
    setConnectionQuality('stable');
    previousQualityRef.current = 'stable';
  }, []);

  // Listen for connection quality changes
  useEffect(() => {
    const handleQualityChange = (newState: SoftphoneConnectionState) => {
      const previousState = previousQualityRef.current;

      // Track status change if different
      if (previousState !== newState) {
        trackConnectionStatusChange(previousState, newState);
        previousQualityRef.current = newState;
      }

      setConnectionQuality(newState);
    };

    VoipMonitorService.on(
      VoipMonitorEvent.CONNECTION_QUALITY_CHANGED,
      handleQualityChange
    );

    return () => {
      VoipMonitorService.removeListener(
        VoipMonitorEvent.CONNECTION_QUALITY_CHANGED,
        handleQualityChange
      );
    };
  }, [trackConnectionStatusChange]);

  // Auto-start monitoring when call becomes active, auto-stop when call ends
  useEffect(() => {
    // During an active conference, individual call sessions have been terminated
    // (REFER/BYE), so their peer connections are closed. Skip monitoring in this case
    // as audio flows through the conference room session, not individual calls.
    const state = useSoftphoneStore.getState();
    const hasActiveConference = state.activeConference !== null;

    if (
      currentCall?.state === 'active' &&
      currentCall.session &&
      !hasActiveConference
    ) {
      startMonitoring();
    }

    return () => {
      // Get fresh state for cleanup check
      const state = useSoftphoneStore.getState();
      const callId = state.currentCallId;
      const call = callId ? state.activeCalls.get(callId) : null;

      if (!call || call.state !== 'active') {
        stopMonitoring();
      }
    };
  }, [
    currentCall?.state,
    currentCall?.session,
    startMonitoring,
    stopMonitoring,
  ]);

  // Track when connection is lost (isConnectionLost becomes true)
  const finalQuality: SoftphoneConnectionState = isConnectionLost
    ? 'off'
    : connectionQuality;

  // Track transition to 'off' state
  useEffect(() => {
    const previousState = previousQualityRef.current;

    if (finalQuality === 'off' && previousState !== 'off') {
      trackConnectionStatusChange(previousState, 'off');
      previousQualityRef.current = 'off';
    }
  }, [finalQuality, trackConnectionStatusChange]);

  return {
    connectionQuality: finalQuality,
    startMonitoring,
    stopMonitoring,
  };
};
